'use client';
import React, { useRef, useEffect } from 'react';

const testimonials = [
  {
    quote: 'This looks more real than most influencer ads I\'ve paid $5,000 for. Genuinely shocked.',
    author: 'Sarah K.',
    role: 'DTC Brand Founder',
    avatar: 'SK',
    avatarColor: 'from-violet-500 to-purple-600',
    stars: 5,
  },
  {
    quote: 'Completely changed my content workflow. I used to spend 3 days on a campaign. Now it\'s 3 hours.',
    author: 'Marcus T.',
    role: 'Social Media Agency Owner',
    avatar: 'MT',
    avatarColor: 'from-pink-500 to-rose-600',
    stars: 5,
  },
  {
    quote: 'Insane realism. My clients can\'t tell the difference between AI UGC and real creator content.',
    author: 'Priya M.',
    role: 'Performance Marketer',
    avatar: 'PM',
    avatarColor: 'from-cyan-500 to-teal-600',
    stars: 5,
  },
  {
    quote: 'The luxury brand campaign system alone is worth 10x the price. Our ROAS went up 340%.',
    author: 'James L.',
    role: 'E-commerce Director',
    avatar: 'JL',
    avatarColor: 'from-amber-500 to-orange-600',
    stars: 5,
  },
  {
    quote: 'I\'ve tried every AI tool out there. Nothing comes close to the realism and workflow quality here.',
    author: 'Aisha R.',
    role: 'Content Creator · 2.1M followers',
    avatar: 'AR',
    avatarColor: 'from-emerald-500 to-green-600',
    stars: 5,
  },
  {
    quote: 'Our agency scaled from 8 to 40 clients in 6 months. AI UGC Studio™ made that possible.',
    author: 'David C.',
    role: 'Creative Agency CEO',
    avatar: 'DC',
    avatarColor: 'from-indigo-500 to-blue-600',
    stars: 5,
  },
];

export default function TestimonialsSection() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('testi-active');
          }
        });
      },
      { threshold: 0.1 }
    );
    cardRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#060609] py-28 px-6 md:px-12 overflow-hidden">
      {/* Ambient */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="font-sans text-xs text-amber-300 tracking-wide">Creator Feedback</span>
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
            What creators are saying.
          </h2>
          <p className="font-sans text-lg text-white/40">
            Real results from real members inside AI UGC Studio™.
          </p>
        </div>

        {/* Testimonial grid — asymmetric */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((t, i) => (
            <div
              key={t.author}
              ref={(el) => { cardRefs.current[i] = el; }}
              className="testi-card group relative rounded-2xl border border-white/8 bg-[#0d0d18] p-6 md:p-7 overflow-hidden"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(circle at 50% 0%, rgba(139,92,246,0.06) 0%, transparent 60%)' }} />

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.stars }).map((_, si) => (
                  <svg key={si} width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1L8.8 5.2L13.5 5.6L10.1 8.5L11.1 13.1L7 10.6L2.9 13.1L3.9 8.5L0.5 5.6L5.2 5.2L7 1Z" fill="#f59e0b" />
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <p className="font-sans text-base text-white/75 leading-relaxed mb-6 relative z-10">
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.avatarColor} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                  {t.avatar}
                </div>
                <div>
                  <div className="font-sans font-semibold text-sm text-white">{t.author}</div>
                  <div className="font-sans text-xs text-white/35">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .testi-card {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .testi-active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
